import { createContext, use, useId, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { useValidationMessage } from '@/i18n/messages';

interface FieldControl {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
}

const FieldContext = createContext<FieldControl | null>(null);

/** Inputs inside a Field get their id, aria-invalid and aria-describedby from it. */
export const useFieldControl = () => use(FieldContext);

export interface FieldProps {
  label: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  /** A react-hook-form error, an i18n key (`validation.xxx`) or ready text. */
  error?: { message?: string } | string;
  /** Message shown in green when the value was checked (UI kit "Checked" state). */
  success?: ReactNode;
  className?: string;
  labelAction?: ReactNode;
  /** Label for screen readers only (compact rows where a placeholder shows what to type). */
  labelHidden?: boolean;
  children: ReactNode;
}

export function Field({
  label,
  required,
  hint,
  error,
  success,
  className,
  labelAction,
  labelHidden = false,
  children,
}: FieldProps) {
  const id = useId();
  const translate = useValidationMessage();
  const message = translate(typeof error === 'string' ? error : error?.message);
  const hintId = `${id}-hint`;
  const describedBy = message || hint || success ? hintId : undefined;

  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <div className={cn('flex items-baseline justify-between gap-3', labelHidden && 'sr-only')}>
        <label htmlFor={id} className="font-medium text-ink">
          {label}
          {required && <span aria-hidden="true"> *</span>}
        </label>
        {labelAction}
      </div>
      <FieldContext value={{ id, describedBy, invalid: Boolean(message) }}>{children}</FieldContext>
      {message ? (
        <p id={hintId} role="alert" className="text-caption font-medium text-danger">
          {message}
        </p>
      ) : success ? (
        <p id={hintId} className="text-caption font-medium text-primary">
          {success}
        </p>
      ) : hint ? (
        <p id={hintId} className="text-caption text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
